import classNames from 'classnames';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { useToggle } from 'hooks/useToggle';
import { useIntl } from 'react-intl';
import TabFormFields from 'routes/essential-workspace/components/header/tab-form-fields';
import { HeaderTabName } from 'routes/essential-workspace/components/header/tab-name';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';

import './style.scss';
import { getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';
import { CustomIconSpinner } from 'utils/customSpinner';

import { HeaderActions } from './actions';

type EssentialHeaderProps = {
  toggleSidePanel: () => void;
  isSidePanelOpened?: boolean;
  isRightPanelOpened: boolean;
  toggleRightPanel: () => void;
  productValidated: boolean;
};

export function EssentialHeader({
  toggleSidePanel,
  isSidePanelOpened = false,
  isRightPanelOpened,
  toggleRightPanel,
  productValidated,
}: EssentialHeaderProps) {
  const { formatMessage } = useIntl();
  const { isOn: isEditingTabName, toggleOn: onEditTabName, toggleOff: onCancelEditTabName } = useToggle(false);

  const activeTabId = useAssertedActiveTabId();
  const loadingStatus = useAppSelector(state => getTabLoadingStatus(state, activeTabId));
  const isTabLoading = loadingStatus === TabLoadingStatus.Loading;

  return (
    <header
      className={classNames(
        'sticky-top flex-x gap-2 p-2 scrollbar-sm essential-header',
        isSidePanelOpened && 'essential-header-side-panel-opened',
      )}
      aria-label="Active form header"
    >
      <div className="h-100 position-absolute header-mask"></div>
      <div className="flex-x flex-grow-1 gap-2 position-relative">
        <button
          className="btn btn-flat-secondary btn-lg btn-icon essential-header-menu"
          type="button"
          aria-label={formatMessage({ id: 'essential.workspace.open.side.panel.btn.ariaLabel' })}
          onClick={toggleSidePanel}
        >
          <span className="material-symbols-outlined">thumbnail_bar</span>
        </button>

        <HeaderTabName
          isEditingTabName={isEditingTabName}
          onCancelEditTabName={onCancelEditTabName}
          onEditTabName={onEditTabName}
        />
        {isTabLoading && <CustomIconSpinner size="sm" className="me-1" color="text-success" />}
      </div>
      <div className="d-flex">
        {productValidated && <TabFormFields />}
        <HeaderActions
          onEditTabName={isEditingTabName ? null : onEditTabName}
          onToggleRightPanel={toggleRightPanel}
          isRightPanelOpened={isRightPanelOpened}
        />
      </div>
    </header>
  );
}
